<script>
  import { onDestroy, onMount } from 'svelte';
  import {
    Search,
    UserPlus,
    Users,
    PanelLeftClose,
    PanelLeftOpen,
    PanelRightClose,
    PanelRightOpen,
    Trash2,
    Forward,
    Phone,
    Video,
    Mic,
    Smile,
    Image as ImageIcon,
    Paperclip,
    MapPin
  } from 'lucide-svelte';
  import LayoutCore from '@components/messenger/chat/desktop/LayoutCore.svelte';
  import {
    sendMessage,
    loadMessagesWS,
    messages,
    status,
    myId,
    myName,
    conversations,
    conversationId,
    replyingTo,
    selectionMode,
    selectedMessageIds,
    getConversationById,
    typingUsers
  } from '$lib/websocket-client.js';

  import { get } from 'svelte/store';

  import { faceStore } from '$lib/stores/face-store';
  import { buildMessengerLayoutOption, messengerLayoutSettings } from '$lib/messenger-layout-settings.js';
  const { menuVisible, toggleMenu, rightVisible, toggleRight } = faceStore;

  import MessageViewport from '@components/messenger/messages/MessageViewport.svelte';
  import Conversion from '@components/messenger/conversations/ConversationItem.svelte';

  import InputMessage from '@components/messenger/messages/MessageComposer.svelte';
  import ConversationSearch from '@components/messenger/searchs/conversations/ConversationSearch.svelte';
  import { filterConversations } from '@components/messenger/searchs/conversations/filter-conversations.js';

  import Popup from '@components/messenger/PopupModel.svelte';
  //import Popup from '@components/messenger/chat/mobile/Popup.svelte';
  import AddUser from '@components/messenger/chat/mobile/AddUser.svelte';
  import AddGroup from '@components/messenger/chat/mobile/AddGroup.svelte';
  import SettingsPanel from '@components/messenger/chat/mobile/SettingsPanel.svelte';
  import ContactPanel from '@components/messenger/chat/mobile/ContactPanel.svelte';
  import ShareMessageToConversation from '@components/messenger/ShareMessageToConversation.svelte';

  import AddUserGroup from '@components/messenger/AddUserGroup.svelte';
  import DraggableWindow from '@components/ui/DraggableWindow.svelte';

  import { startOutgoingCall } from '@components/messenger/calls/call-store.js';

  import StickerPicker from '@components/messenger/StickerPicker.svelte';

  import ShareContent from '@components/messenger/SharedContent.svelte';
  import MessageSearchPanel from '@components/messenger/searchs/messages/MessageSearchPanel.svelte';
  import ConversationEmptyState from '@components/messenger/sharing/ConversationEmptyState.svelte';

  $: typing = $typingUsers[$conversationId];

  //$: console.log('conversations:', $conversations);

  let searchKeyword = '';
  $: filteredConversations = filterConversations($conversations || [], searchKeyword).sort((a, b) => {
    if (a.type === 'cloud') return -1;
    if (b.type === 'cloud') return 1;
    return new Date(b.lastMessageAt || b.createdAt || 0) - new Date(a.lastMessageAt || a.createdAt || 0);
  });

  let showStickerPicker = false;
  let showDesktopToolMenu = false;
  let composerRef;

  function toggleStickerPicker() {
    showDesktopToolMenu = false;
    showStickerPicker = !showStickerPicker;
  }

  function closeStickerPicker() {
    showStickerPicker = false;
  }

  function handleStickerSelect(sticker) {
    sendMessage({
      type: 'chat',
      conversationId: get(conversationId),
      from: get(myId),
      msgType: 'sticker',
      message: sticker.url,
      meta: {
        stickerId: sticker.id
      },
      replyTo: get(replyingTo)?.id || null
    });

    closeStickerPicker();
    replyingTo.set(null);
  }

  // end

  let showSearch = false;

  function toggleSearch() {
    showSearch = !showSearch;
    toggleRight(true);
  }

  function toggleDesktopToolMenu() {
    if (!currentConversation) return;
    closeStickerPicker();
    showDesktopToolMenu = !showDesktopToolMenu;
  }

  function closeDesktopToolMenu() {
    showDesktopToolMenu = false;
  }

  function handleDesktopToolAction(action) {
    closeDesktopToolMenu();

    if (action === 'sticker') {
      showStickerPicker = true;
      return;
    }

    if (action === 'images') {
      composerRef?.pickImages?.();
      return;
    }

    if (action === 'files') {
      composerRef?.pickFiles?.();
      return;
    }

    if (action === 'voice') {
      composerRef?.openVoiceRecorder?.();
      return;
    }

    if (action === 'location') {
      composerRef?.sendCurrentLocation?.();
    }
  }

  $: currentConversation = getConversationById($conversations, $conversationId);

  //$: console.log('currentConversation:', currentConversation);

  let option = {};

  let selected = null;
  $: if ($conversationId && String(selected ?? '') !== String($conversationId)) {
    selected = $conversationId;
  }

  async function selectConversation(conv) {
    if (!conv?.conversationId) return;

    if (String($conversationId ?? '') === String(conv.conversationId)) {
      selected = conv.conversationId;
      return;
    }

    selected = conv.conversationId;
    conversationId.set(selected);

    messages.set([]);
    await loadMessagesWS(selected);
  }

  let showPopup = false;
  let popupType = null;
  let popupMode = 'bottom'; // 'bottom' | 'anchor'
  let popupRect = null;
  let popupOption = {};
  let formOption = {};

  function openForm({ type = '' } = {}) {
    formOption = { type, open: true };
  }

  function openPopup({ type = '', mode = 'bottom', event = null, width = null, height = null } = {}) {
    popupType = type;
    popupMode = mode;
    popupOption = { width, height };

    if (mode === 'anchor' && event?.currentTarget) {
      popupRect = event.currentTarget.getBoundingClientRect();
    } else {
      popupRect = null;
    }

    showPopup = true;
  }

  function closePopup() {
    showPopup = false;
    popupType = null;
    popupRect = null;
  }

  async function handleConversationCreated(conversation) {
    formOption = { ...formOption, open: false, open1: false };
    closePopup();

    if (!conversation?.conversationId) return;
    await selectConversation(conversation);
  }

  const shareMessage = (e) => {
    openPopup({
      type: 'shareConversation',
      mode: 'center',
      width: 'min(90vw, 40rem)',
      height: 'min(86dvh, 45rem)',
      event: e
    });
  };

  $: {
    option = buildMessengerLayoutOption({ replyActive: Boolean($replyingTo) }, $messengerLayoutSettings);
  }

  function handleDesktopPopupOutside(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      target.closest('.desktop-tool-menu') ||
      target.closest('.tool-menu-trigger') ||
      target.closest('.sticker-picker')
    )
      return;
    showDesktopToolMenu = false;
  }

  onMount(() => {
    selected = get(conversationId);
    if (selected && !get(messages).length) {
      loadMessagesWS(selected);
    }

    document.addEventListener('mousedown', handleDesktopPopupOutside);
  });

  onDestroy(() => {
    document.removeEventListener('mousedown', handleDesktopPopupOutside);
  });
</script>

<!-- { mode: 'fixed', lock: true } -->
<LayoutCore {option}>
  <!-- ================= HEADER ================= -->
  <svelte:fragment slot="header">
    <div class="flex h-full min-h-0 w-full items-center justify-between px-3 bg-white/75 backdrop-blur-xl">
      <!-- LEFT: Menu + Name + Status -->
      <div class="flex items-center gap-3 min-w-0">
        <button
          class="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-200 transition"
          onclick={toggleMenu}>
          {#if $menuVisible}
            <PanelLeftClose size={22} />
          {:else}
            <PanelLeftOpen size={22} />
          {/if}
        </button>

        <div class="min-w-0">
          <div class="font-semibold text-slate-900 truncate">2Klo 1</div>
          <div class="text-[11px] text-slate-500 truncate">{$myName}</div>
        </div>

        <span
          class="text-[11px] px-2 py-[2px] rounded-full leading-none whitespace-nowrap
        {$status.includes('Connected') ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}">
          {$status.replace(/\s.*$/, '')}
        </span>
      </div>

      <div class="text-xs font-medium tracking-wide text-slate-400">MY CLOUD MESSENGER</div>

      <!-- RIGHT (placeholder) -->
      <div class="w-9 h-9"></div>
    </div>
  </svelte:fragment>

  <!-- ================= LEFT ================= -->
  <svelte:fragment slot="left">
    <div class="min-w-0 w-full h-full flex flex-col">
      <!-- ===== SEARCH + ACTIONS ===== -->
      <div class="min-h-[58px] px-2 bg-white/75 backdrop-blur-xl flex items-center gap-2 overflow-hidden">
        <!-- 🔍 Search (co giãn đúng chuẩn) -->
        <div class="flex-1 min-w-0 max-w-full">
          <ConversationSearch
            placeholder="Tìm kiếm cuộc trò chuyện"
            onSearch={(keyword) => (searchKeyword = keyword)} />
        </div>

        <!-- Add user -->
        <button
          class="flex-shrink-0 w-9 h-9 flex items-center justify-center
               rounded-full hover:bg-gray-300 transition"
          onclick={(e) => openForm({ type: 'user' })}
          title="Thêm bạn">
          <UserPlus size={20} />
        </button>

        <!-- Create group -->
        <button
          class="flex-shrink-0 w-9 h-9 flex items-center justify-center
               rounded-full hover:bg-gray-300 transition"
          onclick={(e) => openForm({ type: 'group' })}
          title="Tạo nhóm">
          <Users size={20} />
        </button>
      </div>

      <!-- Worklist -->
      <div class="flex-1 bg-slate-50 overflow-auto">
        <div class="w-full h-full overflow-auto">
          <div class="bg-white/70 overflow-hidden">
            <ul id="chat-list" class="divide-y divide-slate-100">
              {#each filteredConversations as conv (conv.conversationId)}
                <Conversion
                  conversation={conv}
                  selectedId={$conversationId}
                  onSelect={selectConversation}
                  className="p-4 rounded-none" />
              {/each}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </svelte:fragment>

  <!-- ================= CHAT HEAD ================= -->
  <svelte:fragment slot="head">
    <div class="h-full w-full flex justify-between items-center gap-3 px-3 bg-white/75 backdrop-blur-xl">
      {#if currentConversation}
        <div class="font-semibold">{currentConversation?.title}</div>
        <div class="font-semibold"></div>
        <div class="">
          <div class="flex items-center gap-2">
            <button
              class="w-9 h-9 flex items-center justify-center
           rounded-full
           hover:bg-gray-300
              transition"
              onclick={() =>
                startOutgoingCall({
                  conversationId: $conversationId,
                  callType: 'audio',
                  title: currentConversation?.title
                })}
              title="Gọi audio">
              <Phone size={20} />
            </button>
            <button
              class="w-9 h-9 flex items-center justify-center
           rounded-full
           hover:bg-gray-300
              transition"
              onclick={() =>
                startOutgoingCall({
                  conversationId: $conversationId,
                  callType: 'video',
                  title: currentConversation?.title
                })}
              title="Gọi video">
              <Video size={20} />
            </button>
            <button
              class="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-200 transition"
              onclick={toggleSearch}
              title="Tìm kiếm trong trò chuyện">
              <Search size={20} class="text-gray-700" />
            </button>

            <button
              class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-300 transition"
              onclick={toggleRight}
              title="Toggle info panel">
              {#if $rightVisible}
                <PanelRightClose size={20} />
              {:else}
                <PanelRightOpen size={20} />
              {/if}
            </button>
          </div>
        </div>
      {/if}
    </div>
  </svelte:fragment>

  <!-- ================= CHAT CONTENT ================= -->
  <svelte:fragment slot="content">
    <div class="h-full min-h-0 w-full flex flex-col relative overflow-hidden">
      <div class="flex-1 min-h-0">
        {#if currentConversation}
          <MessageViewport
            items={$messages}
            conversationId={$conversationId}
            myId={$myId}
            {selected}
            {typing}
            interactionMode="desktop"
            onShareMessage={shareMessage} />
        {:else}
          <ConversationEmptyState
            title="Bắt đầu từ một cuộc trò chuyện"
            description="Chọn một đoạn chat ở cột bên trái để xem tin nhắn, gọi thoại, gọi video và chia sẻ màn hình trên cùng một giao diện." />
        {/if}
      </div>

      {#if $selectionMode}
        <div
          class="h-[56px]
           flex items-center justify-between
           px-4
           bg-white
           border-t border-gray-200
           sticky bottom-0 z-20">
          <!-- LEFT: Cancel -->
          <button
            class="text-sm font-medium text-blue-600 hover:opacity-80"
            onclick={() => {
              selectedMessageIds.set(new Set());
              selectionMode.set(false);
            }}>
            Huỷ
          </button>

          <!-- CENTER: Count -->
          <span class="text-sm text-gray-600">
            {$selectedMessageIds.size} đã chọn
          </span>

          <!-- RIGHT: Actions -->
          <div class="flex items-center gap-2">
            <!-- Share -->
            <button
              class="w-9 h-9
               flex items-center justify-center
               rounded-full
               hover:bg-gray-100
               active:bg-gray-200
               transition"
              onclick={shareMessage}>
              <Forward size="18" class="text-gray-700" />
            </button>

            <!-- Delete -->
            <button
              class="w-9 h-9
               flex items-center justify-center
               rounded-full
               hover:bg-red-50
               active:bg-red-100
               transition"
              onclick={() => {
                const ids = Array.from(get(selectedMessageIds));
                ids.forEach((id) => sendMessage({ type: 'delete', messageId: id }));
                selectedMessageIds.set(new Set());
                selectionMode.set(false);
              }}>
              <Trash2 size="18" class="text-red-600" />
            </button>
          </div>
        </div>
      {/if}

      <!-- STICKER PICKER -->
      {#if showStickerPicker}
        <div class="absolute bottom-[3px] left-3 z-50">
          <StickerPicker onSelect={handleStickerSelect} onClose={closeStickerPicker} />
        </div>
      {/if}

      {#if showDesktopToolMenu && currentConversation}
        <div class="desktop-tool-menu absolute bottom-[3px] right-3 z-50">
          <div
            class="grid min-w-[11rem] gap-1 rounded-2xl border border-slate-200 bg-white/98 p-2 shadow-[0_20px_45px_rgba(15,23,42,0.16)] backdrop-blur-xl">
            <button class="desktop-tool-item" type="button" onclick={() => handleDesktopToolAction('sticker')}>
              <Smile size={18} />
              <span>Sticker</span>
            </button>
            <button class="desktop-tool-item" type="button" onclick={() => handleDesktopToolAction('images')}>
              <ImageIcon size={18} />
              <span>Ảnh</span>
            </button>
            <button class="desktop-tool-item" type="button" onclick={() => handleDesktopToolAction('files')}>
              <Paperclip size={18} />
              <span>Tệp</span>
            </button>
            <button class="desktop-tool-item" type="button" onclick={() => handleDesktopToolAction('voice')}>
              <Mic size={18} />
              <span>Ghi âm</span>
            </button>
            <button class="desktop-tool-item" type="button" onclick={() => handleDesktopToolAction('location')}>
              <MapPin size={18} />
              <span>Gửi vị trí</span>
            </button>
          </div>
        </div>
      {/if}
    </div>
  </svelte:fragment>

  <!-- ================= INPUT ================= -->
  <svelte:fragment slot="comment">
    <div class="h-full w-full flex flex-col">
      {#if currentConversation}
        <InputMessage
          bind:this={composerRef}
          placeholder="Nhập tin nhắn..."
          disabled={!selected}
          toolMenuOpen={showDesktopToolMenu}
          onShowToolMenu={toggleDesktopToolMenu}
          onShowStickerPicker={() => toggleStickerPicker?.()} />
      {:else}
        <div class="h-full flex items-center px-4 text-sm text-slate-400 bg-white/82 border-t border-slate-200/80">
          Chọn một cuộc trò chuyện để bắt đầu nhắn tin, tìm kiếm hoặc chia sẻ nội dung.
        </div>
      {/if}
    </div>
  </svelte:fragment>

  <!-- ================= RIGHT ================= -->
  <svelte:fragment slot="right">
    {#if showSearch}
      <MessageSearchPanel conversationId={$conversationId} onClose={() => (showSearch = false)} />
    {:else}
      <ShareContent conversationId={$conversationId} />
    {/if}
  </svelte:fragment>

  <!-- ================= FOOTER ================= -->
  <div slot="footer"></div>
</LayoutCore>

{#if formOption?.open1}
  <div class="fixed inset-0 bg-black/35 flex items-center justify-center z-[999]">
    <AddUserGroup type={formOption?.type} onClose={() => (formOption.open = false)} />
  </div>
{/if}

{#if formOption?.open}
  <div class="fixed inset-0 bg-black/35 z-[999]">
    <DraggableWindow width="960px" maxHeight="90vh" onClose={() => (formOption.open = false)}>
      <svelte:fragment slot="title">
        <span class="font-semibold">
          {formOption.type === 'group' ? 'Tạo nhóm' : 'Thêm bạn'}
        </span>
      </svelte:fragment>

      <AddUserGroup
        showTitle={false}
        formWidth={false}
        type={formOption.type}
        onClose={() => (formOption.open = false)}
        onSubmit={({ conversation }) => handleConversationCreated(conversation)} />
    </DraggableWindow>
  </div>
{/if}

<!-- POPUP -->
<Popup bind:open={showPopup} mode={popupMode} anchorRect={popupRect} {...popupOption}>
  {#if popupType === 'user'}
    <AddUser onClose={closePopup} onCreated={handleConversationCreated} />
  {:else if popupType === 'group'}
    <AddGroup onClose={closePopup} onCreated={handleConversationCreated} />
    <!-- <StickerPicker onClose={closePopup} /> -->
  {:else if popupType === 'settings'}
    <SettingsPanel onClose={closePopup} />
  {:else if popupType === 'contacts'}
    <ContactPanel onClose={closePopup} />
  {:else if popupType === 'shareConversation'}
    <ShareMessageToConversation variant="desktop" onClose={closePopup} />
  {/if}
</Popup>

<style>
  .desktop-tool-item {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    width: 100%;
    min-height: 2.5rem;
    padding: 0 0.85rem;
    border-radius: 0.95rem;
    color: #334155;
    text-align: left;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      transform 140ms ease;
  }

  .desktop-tool-item:hover {
    color: #0f172a;
    background: #eff6ff;
    transform: translateY(-1px);
  }

  .desktop-tool-item span {
    font-size: 0.86rem;
    font-weight: 600;
  }
</style>
